import config from 'config';
import {
  BaseCard,
  Button,
  FormField,
  GoogleIcon,
  MicrosoftIcon,
  TextInput,
} from 'modules/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AuthBaseCardFooter } from '../../components/AuthBaseCardFooter/AuthBaseCardFooter';
import { AuthBaseCardHeader } from '../../components/AuthBaseCardHeader/AuthBaseCardHeader';
import { AuthLayout } from '../../components/AuthLayout/AuthLayout';
import { SSOSignInPageCSS } from './SSOSignInPage.css';

const { FormWrapper, ProviderButtonContent, Subtitle } = SSOSignInPageCSS;

const getSSOLoginUrl = (organizationId: string, r?: string | null): string => {
  const url = new URL(`${config.apiURL}/sso/saml/login/${organizationId}`);
  if (r) {
    url.searchParams.set('redirectTo', r);
  }
  return url.toString();
};

export const SSOSignInPage = () => {
  const { t } = useTranslation('user', { keyPrefix: 'user.ssoSignIn' });
  const [searchParams] = useSearchParams();
  const [pending, setPending] = useState(false);

  const email = searchParams.get('email');
  const identityProvider = searchParams.get('provider') ?? 'google';
  const organizationId = searchParams.get('organizationId');
  const userExists = searchParams.get('userExists') === 'true';
  const source = searchParams.get('source');
  const r = searchParams.get('r');

  if (!email || !organizationId) {
    return <Navigate to="/login" replace />;
  }

  const handleSignIn = () => {
    setPending(true);
    window.location.href = getSSOLoginUrl(organizationId, r);
  };

  const providerIcon =
    identityProvider === 'microsoft' ? <MicrosoftIcon /> : <GoogleIcon />;

  const providerLabel =
    identityProvider === 'microsoft'
      ? t('signInWithMicrosoft')
      : t('signInWithGoogle');

  const providerDisplayName =
    identityProvider === 'microsoft' ? 'Microsoft' : 'Google';

  return (
    <AuthLayout>
      <BaseCard>
        <AuthBaseCardHeader
          title={userExists ? t('headerUserExists') : t('header')}
        />
        {source === 'signup' && (
          <Subtitle>
            {t('subtitleFromSignup', { provider: providerDisplayName })}
          </Subtitle>
        )}
        <FormWrapper>
          <FormField label={t('email.label')} htmlFor="sso-signin-email">
            <TextInput
              id="sso-signin-email"
              name="email"
              type="email"
              value={email}
              readOnly
              fullWidth
            />
          </FormField>
          <Button
            variant="neutral"
            block
            onClick={handleSignIn}
            disabled={pending}
          >
            <ProviderButtonContent>
              {providerIcon}
              {providerLabel}
            </ProviderButtonContent>
          </Button>
        </FormWrapper>
        <AuthBaseCardFooter
          link={{
            to: source === 'signup' ? '/signup' : '/login',
            label: t('back'),
          }}
        />
      </BaseCard>
    </AuthLayout>
  );
};
